/**
 * Easter emotes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterEmotesPack: ToilePack = {
    slug: 'easter-emotes',
    category: 'season',
    name: 'Easter emotes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'hype', label: 'Hype bunny', subject: 'A round bunny face with wide excited eyes and buck teeth, built to read tiny, isolated'},
        {key: 'crack', label: 'Cracked egg', subject: 'A round egg with a crack and a surprised chick face peeking out, built to read tiny, isolated'},
        {key: 'love', label: 'Chick love', subject: 'A round chick face with heart eyes, built to read tiny, isolated'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'faded rose, sage green, cream and a little gold', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'two or three flat spring colours on white', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a laughing version', 'with tears of joy', 'as a shocked reaction'],
    directionHint: 'try: a laughing version',
    seo: {
        title: 'Easter Emotes: Hype Bunny, Cracked Egg & Chick Love',
        metaDescription: 'Nine Easter emotes — hype bunny, cracked-egg surprise, chick with heart eyes — built to read at 28 pixels, in pastel, vintage, and modern styles.',
        h1: 'Easter twitch emotes: hype bunny, cracked egg, chick love',
        tagline: 'Nine emotes, three moods, three treatments each.',
        intro: 'Nine chat emotes for Easter: a hyped bunny with wide excited eyes, a cracked egg with a surprised chick peeking out, and a chick with heart eyes, each in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Built to survive shrinking down to actual emote size in chat.',
        useCases: [
            "a Twitch channel's Easter emote set",
            "a Discord server's seasonal emoji pack",
            'a sticker reaction for chat and DMs',
            'a small badge on a stream overlay',
        ],
        faq: [
            {q: 'What size do the emotes work at?', a: 'Each design is built and checked to stay readable down to the roughly 28-pixel size Twitch and Discord render emotes at.'},
            {q: 'How many emotes do I get?', a: 'Nine files: three expressions — hype bunny, cracked egg, chick love — each in three visual styles.'},
            {q: 'Can I use these on Twitch and Discord both?', a: 'Yes — the files are plain transparent-background images you upload wherever the platform accepts custom emotes or emoji.'},
            {q: 'Is there text or a watermark on the emotes?', a: 'No — every emote is the face alone with a clean background, ready to upload as-is.'},
        ],
    },
}
